<template>
  <div>
    <customer-show-customer-quote-quicklink :customer="customer"></customer-show-customer-quote-quicklink>
    <div
      class="mb-5 card"
      v-if="can('read_organizations') && white_label_parent && subscription.white_label_organization"
    >
      <div class="card-body">
        <div class="row">
          <div class="col-md-4">
            <h3>{{ 'white_label' | t }}</h3>
            <p class="text-muted">{{ 'white_label_subscription_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <label class="mb-0">{{ 'organization' | t }}</label>
              <p>{{ subscription.white_label_organization.name }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-5 card">
      <div class="card-body">
        <h4 v-if="white_label_parent">{{ 'white_label_subscription' | t }}</h4>
        <h4 v-else>{{ 'subscription' | t }}</h4>
        <template v-if="subscription.header_text">
          <hr />
          <em class="pre-wrap">{{ subscription.header_text }}</em>
        </template>
        <hr />
        <div class="row">
          <div class="col-md-4">
            <h3>{{ 'basic' | t }}</h3>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <label class="mb-0">{{ 'subscription_name' | t }}</label>
              <input class="form-control-plaintext" v-model="subscription.name" :disabled="true" />
            </div>
            <div class="form-group" v-if="!white_label_parent">
              <label class="mb-0">{{ 'plan' | t }}</label>
              <select
                class="form-control"
                v-model="subscription.plan_id"
                @change="fill_subscription(plan)"
                :disabled="true"
              >
                <option v-for="plan in plans" :value="plan.id" :key="plan.id">{{ plan.id }} {{ plan.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'status' | t }}</label>
              <select class="form-control" v-model="subscription.status" :disabled="disabled">
                <option value="trial">{{ 'trial' | t }}</option>
                <option value="active">{{ 'active' | t }}</option>
                <option value="cancelled">{{ 'cancelled' | t }}</option>
                <option value="unbilled">{{ 'unbilled' | t }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'currency' | t }}</label>
              <input class="form-control-plaintext" :value="subscription.currency" :disabled="true" />
            </div>
          </div>
        </div>
        <hr />
        <div class="mt-4 row" v-if="subscription.status == 'active' || subscription.status == 'trial'">
          <div class="col-md-4">
            <h3>{{ 'billing' | t }}</h3>
            <p class="text-muted">{{ 'billing_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <label class="mb-0">{{ 'billing_period' | t }}</label>
              <input class="form-control-plaintext" :value="$t(subscription.billing_period)" />
            </div>
            <template v-if="subscription.status == 'active'">
              <div class="form-group">
                <label class="mb-0">{{ 'billing_period_start' | t }}</label>
                <input
                  class="form-control-plaintext"
                  :value="subscription.billing_period_start | date"
                  :disabled="true"
                />
              </div>
              <div class="form-group">
                <label class="mb-0">{{ 'billing_period_end' | t }}</label>
                <input
                  class="form-control-plaintext"
                  :value="subscription.billing_period_end | date"
                  :disabled="true"
                />
              </div>
              <hr />
            </template>
            <div class="form-group">
              <label class="mb-0">{{ 'usage_period' | t }}</label>
              <input class="form-control-plaintext" :value="$t(subscription.usage_period)" />
            </div>
            <template v-if="subscription.status == 'active'">
              <div class="form-group">
                <div class="form-text">{{ 'usage_period_start' | t }}</div>
                <datetime-picker v-model="subscription.usage_period_start" :dateonly="true"></datetime-picker>
              </div>
              <div class="form-group">
                <div class="form-text">{{ 'usage_period_end' | t }}</div>
                <datetime-picker v-model="subscription.usage_period_end" :dateonly="true"></datetime-picker>
              </div>
            </template>
            <div class="alert alert-warning" v-if="subscription.billing_period != subscription.usage_period">
              {{ 'two_invoices_notice' | t }}
            </div>
            <hr />
            <!-- We don't really support this in the api right now-->
            <div class="form-group" v-if="false">
              <label class="mb-0">{{ 'credit_card_fee' | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="subscription.payment_fee_percentage" :disabled="disabled" />
                <div class="input-group-append">
                  <label class="input-group-text">%</label>
                </div>
              </div>
            </div>
            <div class="form-group" v-if="customer.payment_method == 'invoice'">
              <label class="mb-0">{{ 'bank_payment_fee' | t }}</label>
              <div class="input-group">
                <input class="form-control" v-model="subscription.bank_payment_fee" />
                <div class="input-group-append">
                  <label class="input-group-text">{{ subscription.currency }}</label>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="mb-0">{{ 'billing_product_id' | t }}</label>
              <input class="form-control" :value="subscription.billing_product_id" :disabled="disabled" />
            </div>
          </div>
        </div>
        <div class="mt-4 row" v-else>
          <div class="col-md-4">
            <h3>{{ 'usage_period' | t }}</h3>
            <p class="text-muted">{{ 'usage_period_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <div class="form-text">{{ 'usage_period_start' | t }}</div>
              <datetime-picker v-model="subscription.usage_period_start" :dateonly="true"></datetime-picker>
            </div>
            <div class="form-group">
              <div class="form-text">{{ 'usage_period_end' | t }}</div>
              <datetime-picker v-model="subscription.usage_period_end" :dateonly="true"></datetime-picker>
            </div>
          </div>
        </div>
        <template v-if="!white_label_parent">
          <hr />
          <div class="mt-4 row">
            <div class="col-md-4">
              <h3>{{ 'included_resources' | t }}</h3>
              <p class="text-muted">{{ 'included_resources_subscription_information' | t }}</p>
            </div>
            <div class="col-md-8">
              <div class="form-group" v-for="resource in resources">
                <div v-if="'locked_shipments' === resource">
                  <label class="mb-0">Locked {{ 'periodly_shipments' | t(subscription.usage_period) }}</label>
                  <input class="form-control" v-model="included_locked_shipments" :disabled="disabled" />
                </div>
                <div v-else-if="'shipments' === resource">
                  <label class="mb-0">{{ 'periodly_shipments' | t(subscription.usage_period) | titlecase }}</label>
                  <input class="form-control" v-model="included_shipments" :disabled="disabled" />
                </div>
                <div v-else-if="'orders' === resource">
                  <label class="mb-0">{{ 'periodly_orders' | t(subscription.usage_period) | titlecase }}</label>
                  <input class="form-control" v-model="included_orders" :disabled="disabled" />
                </div>
                <div v-else>
                  <label class="mb-0">{{ `included_${resource}` | t }}</label>
                  <input class="form-control" v-model="subscription[`included_${resource}`]" :disabled="disabled" />
                </div>
              </div>
            </div>
          </div>
        </template>
        <hr />
        <div class="mt-4 row">
          <div class="col-md-4">
            <h3>{{ 'pricing' | t }}</h3>
            <p class="text-muted">{{ 'pricing_subscription_information' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <label class="mb-0">{{ 'recurring_price' | t }}</label>
              <div class="input-group">
                <input
                  class="form-control"
                  v-model="subscription.price"
                  type="number"
                  :disabled="disabled"
                  @keypress="limitInputDecimals"
                />
                <div class="input-group-append">
                  <label class="input-group-text">{{ subscription.currency }}</label>
                </div>
              </div>
            </div>
            <div v-if="!white_label_parent">
              <div class="form-group" v-for="resource in resources">
                <label class="mb-0">{{ `price_${resource}` | t }}</label>
                <div class="input-group">
                  <input
                    class="form-control"
                    v-model="subscription[`price_${resource}`]"
                    type="number"
                    :disabled="disabled"
                    @keypress="limitInputDecimals"
                  />
                  <div class="input-group-append">
                    <label class="input-group-text">{{ subscription.currency }}</label>
                  </div>
                </div>
              </div>
            </div>
            <!-- White label specific fees-->
            <div v-else>
              <div class="form-group">
                <label class="mb-0">{{ 'support_fee' | t }}</label>
                <div class="input-group">
                  <input
                    class="form-control"
                    v-model="subscription.support_fee"
                    type="number"
                    @keypress="limitInputDecimals"
                  />
                  <div class="input-group-append">
                    <label class="input-group-text">{{ subscription.currency }}</label>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label class="mb-0">Price per customer</label>
                <input
                  class="form-control"
                  v-model="subscription.price_customers"
                  type="number"
                  @keypress="limitInputDecimals"
                />
              </div>
              <div class="form-group">
                <label class="mb-0">Price per staff user</label>
                <input
                  class="form-control"
                  v-model="subscription.price_staffs"
                  type="number"
                  @keypress="limitInputDecimals"
                />
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div class="mt-4 row">
          <div class="col-md-4">
            <h3>{{ 'available_features' | t }}</h3>
            <p class="text-muted">{{ 'available_features_description' | t }}</p>
          </div>
          <div class="col-md-8">
            <plans-features-table
              v-model="enabledFeatures"
              :scopes="scopes"
              :disabled="disabled"
            ></plans-features-table>
          </div>
        </div>
        <template v-if="subscription.quote_lines">
          <hr />
          <div class="mt-4 row">
            <div class="col-md-4">
              <h3>{{ 'services' | t }}</h3>
            </div>
            <div class="col-md-8">
              <table class="table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Quantity</th>
                    <th>Unit price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in subscription.quote_lines">
                    <td>{{ line.description || $t(line.key) }}</td>
                    <td>{{ line.quantity }}</td>
                    <td>{{ line.unit_price | currency }} {{ subscription.currency }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
        <div class="mt-4 row" v-if="!disabled">
          <div class="text-right col">
            <div class="btn btn-primary" @click="spin($event, update_subscription)">{{ 'save' | t }}</div>
          </div>
        </div>
      </div>
    </div>
    <div class="mb-5 card">
      <div class="card-body">
        <div class="mt-4 row">
          <div class="col-md-4">
            <h3>{{ 'cancellation' | t }}</h3>
            <p class="text-muted">{{ 'start_cancellation_process' | t }}</p>
          </div>
          <div class="col-md-8">
            <div class="form-group">
              <div class="row" v-if="!subscription.cancellation_date">
                <div class="col">
                  <button
                    class="btn btn-danger"
                    @click="subscription.cancellation_date = subscription.billing_period_end"
                  >
                    {{ 'create_cancellation' | t }}
                  </button>
                </div>
              </div>
              <div class="template" v-else>
                <div class="row">
                  <div class="col-auto">
                    <label>{{ 'cancellation_date' | t }}</label>
                    <datetime-picker class="d-block" v-model="subscription.cancellation_date"></datetime-picker>
                  </div>
                  <div class="col-auto">
                    <label>{{ 'cancellation_cause' | t }}</label>
                    <select class="form-control" v-model="subscription.cancellation_cause">
                      <option v-for="cause in cancellation_causes" :value="cause">{{ cause | t }}</option>
                    </select>
                  </div>
                  <div class="col-auto">
                    <label>{{ 'cancellation_source' | t }}</label>
                    <select class="form-control" v-model="subscription.cancellation_source">
                      <option v-for="source in billing_sources" :value="source">{{ source | t }}</option>
                    </select>
                  </div>
                </div>
                <div class="mt-2 row">
                  <div class="col">
                    <label>{{ 'cancellation_comment' | t }}</label>
                    <textarea class="form-control" v-model="subscription.cancellation_comment"></textarea>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group" v-if="subscription.cancellation_date">
              <div class="mr-2 btn btn-primary" @click="spin($event, save_cancellation)">{{ 'save' | t }}</div>
              <div class="btn btn-primary" @click="spin($event, remove_cancellation)">{{ 'Remove' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Api from '@/store/jsonapi';
import config from '@/config';
import Alerts, { handle } from '@/utils/alerts';
import moment from 'moment-es6';
import { CANCELLATION_CAUSES, BILLING_SOURCES } from '@/utils/billing';
import limitInputDecimals from '@/mixins/limit-input-decimals.js';

export default {
  props: ['subscription', 'currency', 'customer', 'customer_id'],
  mixins: [limitInputDecimals],
  data: function () {
    return {
      plans: [],
      scopes: [],
      passed_go_live_date: false,
      enabledFeatures: [],
      resources: [
        'users',
        'shipments',
        'locked_shipments',
        'orders',
        'carriers',
        'order_channels',
        'printers',
        'automations',
        'return_portals'
      ],
      cancellation_causes: CANCELLATION_CAUSES,
      billing_sources: BILLING_SOURCES
    };
  },
  mounted() {
    Api.findAllPages('plans', {
      include: 'organization',
      fields: { organizations: 'id,name' },
      filter: { currency: this.currency }
    }).then(({ data }) => {
      this.plans = data;

      if (data.length > 0) {
        this.scopes = data[0].meta.feature_scopes;
      }
    });

    this.enabledFeatures = this.getFeaturesAsArray(this.subscription);
  },
  computed: {
    disabled() {
      return !this.can('write_billing');
    },
    white_label_parent() {
      return this.subscription.subscription_type == 'white_label';
    },
    usage_months() {
      return this.get_period_months(this.subscription.usage_period);
    },
    included_shipments: {
      get() {
        if (this.subscription && this.subscription.included_shipments !== null) {
          return Math.round(this.in_usage_period(this.subscription.included_shipments));
        }
        return null;
      },
      set(new_included_shipments) {
        if (new_included_shipments) {
          this.subscription.included_shipments = new_included_shipments / this.usage_months;
        } else {
          this.subscription.included_shipments = 0;
        }
      }
    },
    included_locked_shipments: {
      get() {
        if (this.subscription && this.subscription.included_locked_shipments !== null) {
          return Math.round(this.in_usage_period(this.subscription.included_locked_shipments));
        }
        return null;
      },
      set(new_included_locked_shipments) {
        if (new_included_locked_shipments) {
          this.subscription.included_locked_shipments = new_included_locked_shipments / this.usage_months;

          if (!this.subscription.price_locked_shipments) {
            this.subscription.price_locked_shipments = this.subscription.price_shipments;
          }
        } else {
          this.subscription.included_locked_shipments = null;
        }
      }
    },
    included_orders: {
      get() {
        if (this.subscription && this.subscription.included_orders !== null) {
          return Math.round(this.in_usage_period(this.subscription.included_orders));
        }
        return null;
      },
      set(new_included_orders) {
        if (new_included_orders) {
          this.subscription.included_orders = new_included_orders / this.usage_months;
        } else {
          this.subscription.included_orders = null;
        }
      }
    }
  },
  // Run in beforeMount to avoid datetime-picker updating go_live_date with today
  beforeMount() {
    if (this.subscription.go_live_date) {
      this.passed_go_live_date = moment().isAfter(this.subscription.go_live_date);
    }
  },
  methods: {
    get_period_months(period) {
      switch (period) {
        case 'yearly':
          return 12;
        case 'quarterly':
          return 3;
        default:
          return 1;
      }
    },
    in_usage_period(included) {
      return included * (this.usage_months || 1);
    },
    fill_subscription(plan) {
      _.forOwn(_.omit(plan, 'id', 'created_at', 'updated_at', 'visible'), (value, key) => {
        this.subscription[key] = value;
      });
      this.enabledFeatures = this.getFeaturesAsArray(this.subscription);
    },
    save_cancellation: handle(async function () {
      await Api.update('customers', {
        id: this.customer_id,
        subscription: {
          id: this.customer.subscription.id,
          cancellation_date: this.customer.subscription.cancellation_date,
          cancellation_cause: this.customer.subscription.cancellation_cause,
          cancellation_source: this.customer.subscription.cancellation_source,
          cancellation_comment: this.customer.subscription.cancellation_comment
        }
      });
      Alerts.success({ title: 'Cancellation created', detail: '' });
    }),
    remove_cancellation: handle(async function () {
      this.customer.subscription.cancellation_date = null;
      this.customer.subscription.cancellation_cause = null;
      this.customer.subscription.cancellation_source = null;
      this.customer.subscription.cancellation_comment = null;
      this.customer.subscription.cancellation_created_at = null;
      await Api.update('customers', {
        id: this.customer_id,
        subscription: {
          id: this.customer.subscription.id,
          cancellation_date: null,
          cancellation_cause: null,
          cancellation_source: null,
          cancellation_comment: null,
          cancellation_created_at: null
        }
      });
      Alerts.success({ title: 'Cancellation removed', detail: '' });
    }),

    update_subscription: handle(async function () {
      let subscription = _.omit(this.subscription, 'customer');
      if (!subscription.plan) {
        subscription = _.omit(subscription, 'plan');
      }

      subscription.feature_scopes = this.enabledFeatures.join(' ');

      await Api.update('customers', {
        id: this.customer.id,
        subscription: _.pick(subscription, [
          'id',
          'price',
          'status',
          'price_shipments',
          'price_orders',
          'feature_scopes',
          'price_customers',
          'price_automations',
          'price_carriers',
          'price_locked_shipments',
          'price_order_channels',
          'price_printers',
          'price_return_portals',
          'price_users',
          'support_fee',
          'price_staffs',
          'included_automations',
          'included_shipments',
          'included_orders',
          'included_printers',
          'included_locked_shipments',
          'included_users',
          'included_carriers',
          'included_order_channels',
          'included_return_portals',
          'billing_product_id',
          'bank_payment_fee',
          'usage_period_end',
          'usage_period_start'
        ])
      });

      Alerts.success({ title: 'Subscription was updated', detail: '' });
    }),
    getFeaturesAsArray(subscription) {
      if (subscription && subscription.feature_scopes) {
        return subscription.feature_scopes.split(' ');
      }

      return [];
    }
  }
};
</script>

<style lang="css" scoped>
.pre-wrap {
  white-space: pre-wrap;
}
</style>
